// Manage Recipe Tags sheet — modal pop-up from the Recipes toolbar.
// Sections the user's tags into the three categories the classifier
// understands (effort / cuisine / dish) so adding "burrito" under
// dish or "tex-mex" under cuisine routes the LLM correctly.
//
// Same UX shape as ManageTagsSheet (todos), with the section header +
// category-aware Add input as the only extra wrinkle.

import { useState } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import type { RecipeTag, RecipeTagCategory } from "../../lib/rpc";
import type { UseRecipeTags } from "../../lib/use-recipe-tags";

interface ManageRecipeTagsProps {
  tags: UseRecipeTags;
  notify: (msg: string) => void;
  onClose: () => void;
}

const CATEGORY_SECTIONS: { key: RecipeTagCategory; label: string; sub: string }[] = [
  { key: "effort",  label: "Time / effort", sub: "weeknight / quick / weekend / lazy / veg" },
  { key: "cuisine", label: "Cuisine",       sub: "italian / mexican / asian / your-regional-thing" },
  { key: "dish",    label: "Dish type",     sub: "what's on the plate — burrito, pasta, soup, bowl…" },
];

export function ManageRecipeTagsSheet({ tags, notify, onClose }: ManageRecipeTagsProps) {
  const [drafts, setDrafts] = useState<Record<RecipeTagCategory, string>>({ effort: "", cuisine: "", dish: "" });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  const setDraft = (cat: RecipeTagCategory, v: string) =>
    setDrafts((prev) => ({ ...prev, [cat]: v }));

  const onAdd = async (cat: RecipeTagCategory) => {
    const name = drafts[cat].trim();
    if (!name) return;
    setDraft(cat, "");
    const t = await tags.create(name, cat);
    if (t) notify(`Added #${t.name}`);
  };

  const beginEdit = (t: RecipeTag) => {
    setEditingId(t.id);
    setEditValue(t.name);
  };
  const commitEdit = async () => {
    if (editingId === null) return;
    const v = editValue.trim();
    setEditingId(null);
    if (!v) return;
    const next = await tags.rename(editingId, v);
    if (next) notify(`Renamed to #${next.name}`);
  };

  const onDelete = async (t: RecipeTag) => {
    const ok = await confirm({
      title: `Delete #${t.name}?`,
      message: `Existing recipes with this tag will be untagged. This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const affected = await tags.remove(t.id);
    notify(affected > 0 ? `Deleted #${t.name} · untagged ${affected} recipe${affected === 1 ? "" : "s"}` : `Deleted #${t.name}`);
  };

  const move = (cat: RecipeTagCategory, idx: number, delta: number) => {
    const list = tags.byCategory[cat];
    const swap = idx + delta;
    if (swap < 0 || swap >= list.length) return;
    const next = list.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    void tags.reorder(cat, next.map((t) => t.id));
  };

  return (
    <div className="confirm-bg" onClick={onClose}>
      <div className="manage-tags-modal manage-recipe-tags-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Manage recipe tags">
        <header className="manage-tags-head">
          <h3>Recipe tags</h3>
          <button className="icon-btn" onClick={onClose} type="button" aria-label="Close" title="Close">
            <Icon name="x" size={13} />
          </button>
        </header>

        <p className="manage-tags-sub">
          The auto-tagger reads this list. Add a tag under the right section
          and the next imported recipe can pick it instead of falling back to
          a wrong-but-close match.
        </p>

        <div className="manage-recipe-tags-scroll">
          {CATEGORY_SECTIONS.map(({ key, label, sub }) => (
            <section key={key} className="manage-recipe-tags-section">
              <header className="manage-recipe-tags-section-head">
                <div className="manage-recipe-tags-section-label">{label}</div>
                <div className="manage-recipe-tags-section-sub">{sub}</div>
              </header>
              <div className="manage-tags-list">
                {tags.byCategory[key].map((t, idx) => (
                  <div className="manage-tags-row" key={t.id}>
                    {editingId === t.id ? (
                      <input
                        className="manage-tags-input"
                        value={editValue}
                        autoFocus
                        onInput={(e) => setEditValue((e.currentTarget as HTMLInputElement).value)}
                        onBlur={commitEdit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                          if (e.key === "Escape") { setEditingId(null); }
                        }}
                      />
                    ) : (
                      <button className="manage-tags-name" onClick={() => beginEdit(t)} type="button">
                        #{t.name}
                      </button>
                    )}
                    <div className="manage-tags-actions">
                      <button className="manage-tags-icon" type="button" disabled={idx === 0} onClick={() => move(key, idx, -1)} aria-label="Move up" title="Move up">
                        <Icon name="arrow-up" size={12} />
                      </button>
                      <button className="manage-tags-icon" type="button" disabled={idx === tags.byCategory[key].length - 1} onClick={() => move(key, idx, 1)} aria-label="Move down" title="Move down">
                        <Icon name="arrow-down" size={12} />
                      </button>
                      <button className="manage-tags-icon manage-tags-icon-edit" type="button" onClick={() => beginEdit(t)} aria-label="Rename" title="Rename">
                        <Icon name="edit" size={12} />
                      </button>
                      <button className="manage-tags-icon manage-tags-icon-del" type="button" onClick={() => onDelete(t)} aria-label="Delete" title="Delete">
                        <Icon name="x" size={12} />
                      </button>
                    </div>
                  </div>
                ))}
                {tags.byCategory[key].length === 0 && !tags.loading && (
                  <div className="manage-tags-empty">No {label.toLowerCase()} tags yet.</div>
                )}
              </div>
              <div className="manage-tags-add">
                <input
                  placeholder={`New ${label.toLowerCase()} tag`}
                  value={drafts[key]}
                  onInput={(e) => setDraft(key, (e.currentTarget as HTMLInputElement).value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void onAdd(key); } }}
                  maxLength={32}
                />
                <button className="manage-tags-add-btn" type="button" onClick={() => onAdd(key)} disabled={!drafts[key].trim()}>
                  Add
                </button>
              </div>
            </section>
          ))}
        </div>

        <footer className="manage-tags-foot">
          <button className="manage-tags-done" type="button" onClick={onClose}>Done</button>
        </footer>
      </div>
    </div>
  );
}
